import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import {
  Modal,
  Drawer,
  useToast,
  useConfirm,
  CurrencySelect,
  Select,
  YenUnitToggle,
  YenUnit,
  ErrorState,
} from '@/components/ui';
import { formatMoney as formatMoneyLib } from '@/lib/currency';
import { dealsApi, Deal, DealStage } from '@/lib/dealsApi';
import { accountsApi } from '@/lib/accountsApi';
import { contactsApi } from '@/lib/contactsApi';
import { agentsApi, AgentResponse } from '@/lib/agentsApi';
import { AgentRunPanel } from '@/components/agents/AgentRunPanel';
import { CommentThread } from '@/components/comments/CommentThread';
import { api } from '@/lib/api';
import { formatLocaleDate } from '@/lib/date';

function formatMoney(
  amountCents: number | null,
  currency: string,
  locale: string,
  unit: YenUnit = '1',
) {
  if (amountCents == null) return '—';
  const v = amountCents / (currency === 'JPY' ? 1 : 100);
  return formatMoneyLib(v, { currency, locale, unit });
}

interface DealFormState {
  name: string;
  accountId: string;
  primaryContactId: string;
  stage: string;
  amount: string;
  currency: string;
  expectedClose: string;
  probability: number;
}

const EMPTY_FORM: DealFormState = {
  name: '',
  accountId: '',
  primaryContactId: '',
  stage: '',
  amount: '',
  currency: 'JPY',
  expectedClose: '',
  probability: 50,
};

function DealForm({
  wsId,
  stages,
  initial,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  wsId: string;
  stages: DealStage[];
  initial: DealFormState;
  onCancel: () => void;
  onSubmit: (values: DealFormState) => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<DealFormState>(initial);

  // Load all accounts once (limit 200 is enough for the picker — at
  // higher scale we'd want server-side typeahead, but the polished
  // Select already searches the option list client-side).
  const accounts = useQuery({
    queryKey: ['accounts', wsId, 'deal-picker'],
    queryFn: () =>
      accountsApi.list({
        workspaceId: wsId,
        limit: 200,
      }),
    enabled: !!wsId,
  });

  const accountOptions = useMemo(
    () =>
      (accounts.data?.items ?? []).map((a) => ({
        value: a.id,
        label: a.legalName,
        hint: a.domain ?? undefined,
        searchAliases: [a.domain ?? '', a.jpCorpNumber ?? ''].filter(Boolean) as string[],
      })),
    [accounts.data],
  );

  const contacts = useQuery({
    queryKey: ['contacts', wsId, 'by-account', form.accountId],
    queryFn: () =>
      contactsApi.list({ workspaceId: wsId, accountId: form.accountId, limit: 50 }),
    enabled: !!wsId && !!form.accountId,
  });

  const set = <K extends keyof DealFormState>(k: K, v: DealFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length > 0 &&
    form.accountId &&
    form.stage &&
    !submitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(form);
      }}
      className="space-y-4"
    >
      <label className="block">
        <span className="text-xs text-ink-600">
          {t('deals.field_name')} <span className="text-warn-600">*</span>
        </span>
        <input
          type="text"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
          className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
      </label>

      <div>
        <span className="text-xs text-ink-600">
          {t('deals.field_account')} <span className="text-warn-600">*</span>
        </span>
        <div className="mt-1">
          <Select
            value={form.accountId}
            onChange={(v) => {
              set('accountId', v);
              set('primaryContactId', '');
            }}
            options={accountOptions}
            placeholder={t('people.account_search_placeholder')}
          />
        </div>
      </div>

      <div>
        <span className="text-xs text-ink-600">{t('deals.field_primary_contact')}</span>
        <div className="mt-1">
          <Select
            value={form.primaryContactId}
            onChange={(v) => set('primaryContactId', v)}
            disabled={!form.accountId}
            placeholder={t('common.none')}
            options={[
              { value: '', label: t('common.none') },
              ...(contacts.data?.items ?? []).map((c) => ({
                value: c.id,
                label: c.displayName,
                hint: c.email ?? undefined,
              })),
            ]}
          />
        </div>
        {!form.accountId && (
          <span className="text-[11px] text-ink-500">
            {t('deals.account_required_for_contact')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs text-ink-600">
            {t('deals.field_stage')} <span className="text-warn-600">*</span>
          </span>
          <select
            value={form.stage}
            onChange={(e) => set('stage', e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          >
            <option value="">{t('common.select_placeholder')}</option>
            {stages.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('deals.field_amount')}</span>
          <input
            type="number"
            min={0}
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('deals.field_currency')}</span>
          <div className="mt-1">
            <CurrencySelect
              value={form.currency}
              onChange={(v) => set('currency', v)}
            />
          </div>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-ink-600">{t('deals.field_expected_close')}</span>
          <input
            type="date"
            value={form.expectedClose}
            onChange={(e) => set('expectedClose', e.target.value)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">
            {t('deals.field_probability')}: <span className="num">{form.probability}%</span>
          </span>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={form.probability}
            onChange={(e) => set('probability', Number(e.target.value))}
            className="mt-1 w-full"
          />
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function DealDrawerBody({
  wsId,
  deal,
  stages,
}: {
  wsId: string;
  deal: Deal;
  stages: DealStage[];
}) {
  const { t, i18n } = useTranslation();
  const coachToast = useToast();
  const [coach, setCoach] = useState<AgentResponse<{
    healthScore: number;
    nextBestActions: string[];
    rationale: string;
  }> | null>(null);

  const account = useQuery({
    queryKey: ['account', deal.accountId],
    queryFn: () => accountsApi.get(deal.accountId),
    enabled: !!deal.accountId,
  });

  const run = useMutation({
    mutationFn: () =>
      agentsApi.dealCoach({ workspaceId: wsId, dealId: deal.id }),
    onSuccess: (r) => setCoach(r),
    onError: () => coachToast.error(t('agents.run_failed', { defaultValue: 'AI run failed. Please try again.' })),
  });

  const stage = stages.find((s) => s.key === deal.stage);

  return (
    <div className="space-y-6 text-sm">
      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('deals.drawer_overview')}
        </h3>
        <dl className="space-y-1.5">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('deals.field_account')}</dt>
            <dd className="text-ink-900">{account.data?.legalName ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('deals.field_stage')}</dt>
            <dd className="text-ink-900">{stage?.label ?? deal.stage}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('deals.field_amount')}</dt>
            <dd className="text-ink-900 num">
              {formatMoney(deal.amountCents, deal.currency, i18n.language)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('deals.field_probability')}</dt>
            <dd className="text-ink-900 num">
              {deal.probability != null ? `${deal.probability}%` : '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('deals.field_expected_close')}</dt>
            <dd className="text-ink-900 num">
              {deal.expectedClose
                ? formatLocaleDate(deal.expectedClose, i18n.language)
                : '—'}
            </dd>
          </div>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('deals.drawer_deal_coach')}
        </h3>
        <p className="text-xs text-ink-600 mb-2">
          {t('deals.drawer_deal_coach_intro')}
        </p>
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100 disabled:opacity-60"
        >
          {run.isPending ? t('deals.coach_running') : t('deals.run_deal_coach')}
        </button>

        {(coach || run.isPending) && (
          <div className="mt-4">
            <AgentRunPanel
              response={coach ?? undefined}
              isPending={run.isPending}
              prompt={`deal:${deal.id} deal-coach`}
            />
          </div>
        )}
      </section>

      {/* Comments + @mentions on the deal record. */}
      <section className="pt-2 border-t border-ink-200">
        <CommentThread workspaceId={wsId} targetKind="deal" targetId={deal.id} />
      </section>
    </div>
  );
}

export function DealsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [createOpen, setCreateOpen] = useState(false);
  const [drawerDeal, setDrawerDeal] = useState<Deal | null>(null);
  // Display unit for amounts in the kanban — only visible when the
  // workspace's reporting currency is JPY. The bucket totals + every
  // deal amount get formatted at the selected unit.
  const [amountUnit, setAmountUnit] = useState<YenUnit>('1');

  const pipeline = useQuery({
    queryKey: ['deals', 'pipeline', wsId],
    queryFn: () => dealsApi.pipeline(wsId),
    enabled: !!wsId,
  });

  const seedStages = useMutation({
    mutationFn: () => dealsApi.seedDefaultStages(wsId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] }),
  });

  const [stagesOpen, setStagesOpen] = useState(false);
  const [newStageLabel, setNewStageLabel] = useState('');

  const stages = useMemo(() => pipeline.data?.stages ?? [], [pipeline.data]);

  const addStage = useMutation({
    mutationFn: (label: string) =>
      dealsApi.createStage({
        workspaceId: wsId,
        key: label
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 50) || `stage_${stages.length + 1}`,
        label: label.trim(),
        position: stages.length,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] });
      setNewStageLabel('');
      toast.success(t('deals.stage_added'));
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const removeStage = useMutation({
    mutationFn: (key: string) => dealsApi.deleteStage(wsId, key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] });
      toast.success(t('deals.stage_deleted'));
    },
    onError: () => toast.error(t('deals.stage_delete_failed')),
  });
  const buckets = pipeline.data?.buckets ?? {};
  const initialForm: DealFormState = useMemo(
    () => ({ ...EMPTY_FORM, stage: stages[0]?.key ?? '' }),
    [stages],
  );

  const create = useMutation({
    mutationFn: (v: DealFormState) => {
      const amountCents = v.amount
        ? Math.round(parseFloat(v.amount) * (v.currency === 'JPY' ? 1 : 100))
        : null;
      return dealsApi.create({
        workspaceId: wsId,
        accountId: v.accountId,
        name: v.name,
        stage: v.stage,
        primaryContactId: v.primaryContactId || null,
        amountCents,
        currency: v.currency,
        probability: v.probability,
        expectedClose: v.expectedClose || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] });
      setCreateOpen(false);
      toast.success(t('deals.created'));
    },
    onError: () => toast.error(t('deals.create_failed')),
  });

  const moveStage = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) =>
      dealsApi.update(id, { stage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] });
      toast.success(t('deals.updated'));
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      // The dealsApi client doesn't expose a remove() helper yet, so call the
      // backend's DELETE /api/deals/:id directly through the shared axios client.
      await api.delete(`/api/deals/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'pipeline', wsId] });
      toast.success(t('deals.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDelete = async (d: Deal) => {
    const ok = await confirm({
      title: t('deals.confirm_delete_title'),
      description: t('deals.confirm_delete_body', { name: d.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(d.id);
  };

  return (
    <AppShell titleKey="deals.title">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-ink-600">{t('deals.intro')}</p>
        <div className="flex items-center gap-2">
          {/* JPY-only display-unit toggle; hides itself for non-JPY. */}
          <YenUnitToggle
            value={amountUnit}
            onChange={setAmountUnit}
            currency="JPY"
          />
          {stages.length === 0 && (
            <button
              type="button"
              onClick={() => seedStages.mutate()}
              disabled={seedStages.isPending}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
            >
              {seedStages.isPending ? t('deals.seeding') : t('deals.seed_button')}
            </button>
          )}
          {stages.length > 0 && (
            <button
              type="button"
              onClick={() => setStagesOpen(true)}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
            >
              {t('deals.manage_stages')}
            </button>
          )}
          {stages.length > 0 && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
            >
              {t('deals.new_button')}
            </button>
          )}
        </div>
      </div>

      <Modal
        open={stagesOpen}
        onOpenChange={setStagesOpen}
        title={t('deals.manage_stages')}
      >
        <div className="space-y-2">
          {stages.map((s) => (
            <div key={s.key} className="flex items-center gap-2 py-1.5 border-b border-ink-100 last:border-0">
              <span className="flex-1 text-sm text-ink-800">{s.label}</span>
              {(s.isWon || s.isLost) && (
                <span className="text-[10px] uppercase tracking-wide text-ink-400">
                  {s.isWon ? t('deals.stage_won') : t('deals.stage_lost')}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeStage.mutate(s.key)}
                disabled={removeStage.isPending}
                className="text-xs px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          ))}
        </div>
        <form
          className="mt-4 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newStageLabel.trim()) addStage.mutate(newStageLabel);
          }}
        >
          <input
            value={newStageLabel}
            onChange={(e) => setNewStageLabel(e.target.value)}
            placeholder={t('deals.stage_name_placeholder')}
            className="flex-1 rounded-md border border-ink-300 px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={addStage.isPending || !newStageLabel.trim()}
            className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50"
          >
            {t('deals.add_stage')}
          </button>
        </form>
      </Modal>

      {pipeline.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {pipeline.isError && <ErrorState onRetry={() => pipeline.refetch()} />}

      {stages.length === 0 && !pipeline.isLoading && !pipeline.isError && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('deals.empty_title')}</p>
          <p className="text-xs mt-2">{t('deals.empty_sub')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {stages
          .filter((s) => !s.isWon && !s.isLost)
          .map((stage) => {
            const bucket =
              buckets[stage.key] || { count: 0, totalAmountCents: 0, deals: [] };
            return (
              <div
                key={stage.key}
                className="rounded-2xl bg-white border border-ink-200 p-3 min-h-[200px]"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-ink-900 uppercase tracking-wide">
                    {stage.label}
                  </div>
                  <div className="text-xs text-ink-500 num">{bucket.count}</div>
                </div>
                <div className="text-xs text-ink-500 mb-3 num">
                  {t('deals.total_prefix')}{' '}
                  {formatMoney(bucket.totalAmountCents, 'JPY', i18n.language, amountUnit)}
                </div>
                <ul className="space-y-2">
                  {bucket.deals.map((d) => (
                    <li
                      key={d.id}
                      className="rounded-md border border-ink-100 p-2 hover:border-accent-300 hover:bg-accent-50 transition cursor-pointer group"
                      onClick={() => setDrawerDeal(d)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-ink-900 truncate">
                            {d.name}
                          </div>
                          <div className="text-xs num text-ink-600">
                            {formatMoney(
                              d.amountCents,
                              d.currency,
                              i18n.language,
                              d.currency === 'JPY' ? amountUnit : '1',
                            )} ·{' '}
                            {d.probability ?? '—'}
                            {t('deals.probability_unit')}
                          </div>
                          {d.nextBestAction && (
                            <div className="mt-1 text-[11px] text-accent-700 truncate">
                              → {d.nextBestAction}
                            </div>
                          )}
                        </div>
                        <div
                          className="shrink-0 flex flex-col items-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button
                                type="button"
                                aria-label={t('deals.stage_change_label')}
                                className="rounded p-0.5 text-ink-500 hover:bg-ink-100"
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content
                                align="end"
                                sideOffset={4}
                                className="min-w-[160px] rounded-md border border-ink-200 bg-white p-1 text-sm"
                              >
                                <DropdownMenu.Label className="px-2 py-1 text-[11px] uppercase tracking-wide text-ink-500">
                                  {t('deals.stage_change_label')}
                                </DropdownMenu.Label>
                                {stages.map((s) => (
                                  <DropdownMenu.Item
                                    key={s.key}
                                    disabled={s.key === d.stage}
                                    onSelect={() =>
                                      moveStage.mutate({ id: d.id, stage: s.key })
                                    }
                                    className="cursor-pointer rounded-sm px-2 py-1 text-ink-700 outline-none data-[highlighted]:bg-ink-100 data-[disabled]:opacity-50 data-[disabled]:pointer-events-none"
                                  >
                                    {s.label}
                                  </DropdownMenu.Item>
                                ))}
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button
                                type="button"
                                aria-label={t('deals.row_actions')}
                                className="rounded p-0.5 text-ink-500 hover:bg-ink-100"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content
                                align="end"
                                sideOffset={4}
                                className="min-w-[140px] rounded-md border border-ink-200 bg-white p-1 text-sm"
                              >
                                <DropdownMenu.Item
                                  onSelect={() => askDelete(d)}
                                  className="cursor-pointer rounded-sm px-2 py-1 text-warn-600 outline-none data-[highlighted]:bg-warn-50"
                                >
                                  {t('common.delete')}
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
      </div>

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('deals.new_title')}
        size="lg"
      >
        <DealForm
          wsId={wsId}
          stages={stages}
          initial={initialForm}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(v) => create.mutate(v)}
          submitting={create.isPending}
          submitLabel={create.isPending ? t('common.creating') : t('common.create')}
        />
      </Modal>

      <Drawer
        open={!!drawerDeal}
        onOpenChange={(o) => !o && setDrawerDeal(null)}
        title={drawerDeal?.name ?? ''}
      >
        {drawerDeal && (
          <DealDrawerBody wsId={wsId} deal={drawerDeal} stages={stages} />
        )}
      </Drawer>
    </AppShell>
  );
}
